import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Alert,Box, Button, Group, PasswordInput, Progress, Stack, Text, Title } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowRight,IconX } from "@tabler/icons-react";

import { ErrorPage } from "@pages/ErrorPage";

import { AppLoader } from "@components/AppLoader";
import { LanguageToggle } from "@components/LanguageToggle";

import { getOnboardingAccessToken } from "@utils/api";

import { useReopenSession } from "@hooks/mutations/onboarding/useReopenSession";
import { useSetOnboardingPassword } from "@hooks/mutations/onboarding/useSetOnboardingPassword";
import { useFinalizeOnboarding } from "@hooks/mutations/onboarding/useFinalizeOnboarding";
import { useSetupOnboardingPayout } from "@hooks/mutations/onboarding/useSetupOnboardingPayout";
import { useGetMySession } from "@hooks/queries/onboarding/useGetMySession";

import { useAuth } from "@contexts/auth";

import { Step1Registration } from "./components/Step1Registration";
import { Step2ProfessionalCategory } from "./components/Step2ProfessionalCategory";
import { Step3ProfileDraft } from "./components/Step3ProfileDraft";
import { Step4Services } from "./components/Step4Services";
import { Step5Availability } from "./components/Step5Availability";
import { Step6Uploads } from "./components/Step6Uploads";
import { Step7Contract } from "./components/Step7Contract";
import { StepMobileHandoff } from "./components/StepMobileHandoff";
import { ResumeOnboarding } from "./components/ResumeOnboarding";
import { StyledOnboardingContainer } from "./Onboarding.styled";


const RejectedScreen = ({ reason, onFix }: { reason: string, onFix: () => void }) => {
  const { t } = useTranslation();
  return (
    <Stack align="center" py="xl">
      <Group justify="center" bg="red.0" w={80} h={80} style={{ borderRadius: "100%" }}>
        <IconX size={40} color="var(--mantine-color-red-6)" />
      </Group>
      <Title order={3} ta="center" mt="md">{t("onboarding-needs-revision")}</Title>
      <Text ta="center" c="dimmed" mt="sm">
        {t("onboarding-needs-revision-desc")}
      </Text>
      
      <Box mt="md" p="md" style={{ background: "#fff5f5", border: "1px solid #ffc9c9", borderRadius: "8px", width: "100%" }}>
        <Text fw={600} c="red.9" size="sm" mb="xs">{t("onboarding-admin-feedback")}</Text>
        <Text size="sm" c="red.8">{reason}</Text>
      </Box>

      <Button mt="xl" size="md" rightSection={<IconArrowRight size={16} />} onClick={onFix}>
        {t("onboarding-review-update")}
      </Button>
    </Stack>
  );
};

const PasswordSetupPrompt = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const { mutate: setPasswordMutation, isLoading, error: requestError } = useSetOnboardingPassword();

  const handleSubmit = () => {
    if (password.length < 8) {
      setError(t("password-minimum-error", "Password must be at least 8 characters."));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("passwords-do-not-match", "Passwords do not match."));
      return;
    }

    setError("");
    setPasswordMutation({ password });
  };

  return (
    <Stack w="100%" maw={420} mt="lg">
      <Alert color="blue" variant="light">
        {t("onboarding-set-password-desc")}
      </Alert>
      {Boolean(requestError) && <Alert color="red">{(requestError as any)?.response?.data?.detail || t("onboarding-password-error")}</Alert>}
      <PasswordInput
        label={t("password")}
        placeholder={t("password-placeholder")}
        value={password}
        onChange={event => setPassword(event.currentTarget.value)}
        error={error && password.length < 8 ? error : undefined}
      />
      <PasswordInput
        label={t("confirm-password")}
        placeholder={t("confirm-password-placeholder")}
        value={confirmPassword}
        onChange={event => setConfirmPassword(event.currentTarget.value)}
        error={error && password.length >= 8 ? error : undefined}
      />
      <Button onClick={handleSubmit} loading={isLoading}>
        {t("onboarding-create-password")}
      </Button>
    </Stack>
  );
};

const FinalizationPrompt = () => {
  const { t } = useTranslation();
  const { mutate: finalize, isLoading, error } = useFinalizeOnboarding();
  return (
    <Stack align="center" py="xl">
      <Title order={3} ta="center">{t("onboarding-password-saved")}</Title>
      <Text ta="center" c="dimmed">{t("onboarding-finalize-desc")}</Text>
      {Boolean(error) && <Alert color="red">{(error as any)?.response?.data?.detail || t("onboarding-finalize-error")}</Alert>}
      <Button loading={isLoading} onClick={() => finalize()}>{t("onboarding-finish-setup")}</Button>
    </Stack>
  );
};

const PayoutSetupPrompt = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: setupPayout, isLoading, error } = useSetupOnboardingPayout();
  return (
    <Stack align="center" py="xl">
      <Title order={3} ta="center">{t("onboarding-account-ready")}</Title>
      <Text ta="center" c="dimmed">{t("onboarding-payout-pending")}</Text>
      {Boolean(error) && <Alert color="red">{(error as any)?.response?.data?.detail || t("onboarding-payout-error")}</Alert>}
      <Button loading={isLoading} onClick={() => setupPayout()}>{t("onboarding-retry-payout")}</Button>
      <Button variant="subtle" onClick={() => navigate("/dashboard/staff/overview")}>{t("onboarding-go-dashboard")}</Button>
    </Stack>
  );
};

export const OnboardingWizard = () => {
  const { t } = useTranslation();
  const { token: authToken, onboardingStatus } = useAuth();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token");
  const [onboardingToken, setOnboardingToken] = useState(() => getOnboardingAccessToken());
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Dynamically inject the mobile viewport meta tag ONLY for the Onboarding flow
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    let wasCreated = false;

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'viewport');
      meta.setAttribute('content', 'width=device-width, initial-scale=1.0');
      document.head.appendChild(meta);
      wasCreated = true;
    }

    return () => {
      // Clean up the meta tag when the user leaves Onboarding (so the main EMR dashboard stays in desktop mode)
      if (wasCreated && meta) {
        meta.remove();
      }
    };
  }, []);

  // Call the query unconditionally, but only run it if the user is authenticated
  const { data: sessionData, isLoading, isError } = useGetMySession(!!authToken || !!onboardingToken);
  const { mutate: reopenSession } = useReopenSession();

  const handleFixApplication = () => {
    reopenSession();
  };

  // If the user is fully approved, they shouldn't be here. Send them to the dashboard.
  if (onboardingStatus === "approved" && authToken && sessionData?.status === "approved" && sessionData.payout_setup_state !== "pending") {
    return <Navigate to="/dashboard/staff/overview" replace />;
  }

  // If they are not logged in and have no URL token, send them to sign in.
  if (!authToken && !urlToken && !onboardingToken) {
    return <StyledOnboardingContainer><LanguageToggle /><div className="onboarding-card">
      <Title order={2} mb="md">{t("onboarding-resume")}</Title>
      <ResumeOnboarding onResumed={() => setOnboardingToken(getOnboardingAccessToken())} />
    </div></StyledOnboardingContainer>;
  }

  // 1. Mobile Interception (Run early)
  // We want to block mobile users *after* Step 1.
  // If they are authenticated (i.e. finished Step 1) AND on mobile, we show the handoff.
  // Note: We still let them see the loader/data if they are 'pending_review' or 'rejected' 
  // so they can read admin feedback. But if they just finished Step 1 (status = account_created), we block them.
  const isMobileHandoffRequired = isMobile && authToken && sessionData?.status !== "pending_review" && sessionData?.status !== "rejected";

  if (isMobileHandoffRequired) {
    // Only show handoff once sessionData is loaded so we don't accidentally block someone trying to read their rejection reason.
    if (isLoading) return <AppLoader />;
    return (
      <StyledOnboardingContainer>
        <LanguageToggle />
        <div className="onboarding-card">
          <StepMobileHandoff />
        </div>
      </StyledOnboardingContainer>
    );
  }

  // 2. Unauthenticated Token Flow (Step 1)
  if (!authToken && urlToken && !onboardingToken) {
    return (
      <StyledOnboardingContainer>
        <LanguageToggle />
        <div className="onboarding-card">
          <Title order={2} ta="center" mb="md">
            {t("onboarding-welcome")}
          </Title>
          <Text ta="center" c="dimmed" mb="xl">
            {t("onboarding-setup")}
          </Text>
          <Step1Registration token={urlToken} onStarted={() => setOnboardingToken(getOnboardingAccessToken())} />
        </div>
      </StyledOnboardingContainer>
    );
  }

  // 3. Authenticated Draft Flow (Steps 2 - 7)
  if (isLoading) return <AppLoader />;
  if (isError || !sessionData) {
    if (!authToken) return <StyledOnboardingContainer><LanguageToggle /><div className="onboarding-card">
      <Alert color="red" mb="md">{t("onboarding-session-recovery")}</Alert>
      <ResumeOnboarding onResumed={() => setOnboardingToken(getOnboardingAccessToken())} />
    </div></StyledOnboardingContainer>;
    return <ErrorPage />;
  }

  // Determine active step based on backend status
  let activeStep = 0;
  if (sessionData.current_step === 2) activeStep = 0; // Category Step
  if (sessionData.current_step === 3) activeStep = 1; // Profile Step
  if (sessionData.current_step === 4) activeStep = 2; // Services Step
  if (sessionData.current_step === 5) activeStep = 3; // Availability Step
  if (sessionData.current_step === 6) activeStep = 4; // Uploads Step
  if (sessionData.current_step === 7) activeStep = 5; // Contract Step

  const steps = [
    { title: t("onboarding-prof-cat"), component: <Step2ProfessionalCategory sessionData={sessionData} /> },
    { title: t("onboarding-prof-details"), component: <Step3ProfileDraft sessionData={sessionData} /> },
    { title: t("onboarding-services-prices"), component: <Step4Services sessionData={sessionData} /> },
    { title: t("onboarding-schedule-availability"), component: <Step5Availability sessionData={sessionData} /> },
    { title: t("onboarding-verification"), component: <Step6Uploads sessionData={sessionData} /> },
    { title: t("onboarding-review-sign"), component: <Step7Contract sessionData={sessionData} /> },
  ];

  const totalSteps = steps.length;
  const progressValue = ((activeStep + 1) / totalSteps) * 100;

  return (
    <StyledOnboardingContainer>
      <LanguageToggle />
      <div className="onboarding-card">
        {sessionData.status === "approved" && sessionData.payout_setup_state === "pending" ? (
          <PayoutSetupPrompt />
        ) : sessionData.status === "approved" && authToken ? (
          <FinalizationPrompt />
        ) : sessionData.status === "pending_review" && sessionData.user_id && authToken ? (
          <FinalizationPrompt />
        ) : sessionData.status === "pending_review" ? (
          <Stack align="center" py="xl">
            <Title order={3} ta="center">{t("onboarding-contract-saved")}</Title>
            <Text ta="center" c="dimmed" mt="sm" mb="xl">
              {t("onboarding-create-password-next")}
            </Text>
            <PasswordSetupPrompt />
          </Stack>
        ) : sessionData.status === "rejected" ? (
          <RejectedScreen reason={sessionData.rejection_reason} onFix={handleFixApplication} />
        ) : activeStep >= totalSteps ? (
          <Stack align="center" py="xl">
            <Title order={3} ta="center">{t("onboarding-ready-submission")}</Title>
            <Text ta="center" c="dimmed" mt="sm" mb="xl">
              {t("onboarding-success-details")}
            </Text>
          </Stack>
        ) : (
          <>
            <Title order={2} mb="xs">
              {t("onboarding-complete-profile")}
            </Title>
            
            <Box mb="xl">
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  {t("onboarding-step")} {activeStep + 1} {t("onboarding-of")} {totalSteps}: <Text span c="dark" fw={600}>{steps[activeStep].title}</Text>
                </Text>
                <Text size="sm" c="dimmed" fw={500}>{Math.round(progressValue)}%</Text>
              </Group>
              <Progress value={progressValue} size="md" radius="xl" color="primary" />
            </Box>
            
            <Box mt="lg">
              {steps[activeStep].component}
            </Box>
          </>
        )}
      </div>
    </StyledOnboardingContainer>
  );
};
